import {
	designTokens,
	Flex,
	RiAlertFill,
	RiErrorWarningFill
} from "@itandi/itandi-bb-ui";
import Link from "next/link";
import Styles from "./StatusCard.module.scss";

type TodoStatusCardVariant = "default" | "past" | "today";

type Props = {
	count: number;
	href: string;
	label: string;
	variant: TodoStatusCardVariant;
};

const baseCustomProperties = {
	"--background": designTokens["color/background/default/white"],
	"--border-radius": `${designTokens["border-radius/s"]}px`,
	"--font-size-m": `${designTokens["text/label/m"]}px`,
	"--font-size-xxl": `${designTokens["text/label/xxl"]}px`,
	"--icon-size": "24px",
	"--padding": `${designTokens["spacing/m"]}px`,
	"--shadow": designTokens["elevation/1"]
} as const satisfies Record<string, string | number>;

const variantCustomProperties = {
	default: {
		"--color-text-subtle": designTokens["color/text/subtle"],
		"--hover-background": designTokens["color/background/info/subtle"],
		"--link-color": designTokens["color/text/link"]
	},
	past: {
		"--border-top": `${designTokens["spacing/xxs"]}px solid ${designTokens["color/border/danger"]}`,
		"--color-text-subtle": designTokens["color/text/danger"],
		"--count-color": designTokens["color/text/danger"],
		"--hover-background": designTokens["color/background/danger/subtle"],
		"--icon-color": designTokens["color/text/danger"],
		"--link-color": designTokens["color/text/danger"]
	},
	today: {
		"--border-top": `${designTokens["spacing/xxs"]}px solid ${designTokens["color/border/warning"]}`,
		"--color-text-subtle": designTokens["color/text/warning"],
		"--count-color": designTokens["color/text/warning"],
		"--hover-background": designTokens["color/background/warning/subtle"],
		"--icon-color": designTokens["color/text/warning"],
		"--link-color": designTokens["color/text/warning"]
	}
} as const satisfies Record<
	TodoStatusCardVariant,
	Record<string, string | number>
>;

const getCustomProperties = (
	variant: TodoStatusCardVariant
): Record<string, string | number> => ({
	...baseCustomProperties,
	...variantCustomProperties[variant]
});
/**
 * ダッシュボード画面用のステータスカードコンポーネント
 *
 * @for client, vendor
 */

export const StatusCard: React.FC<Props> = ({
	label,
	href,
	count,
	variant
}: Props) => {
	const customProperties = getCustomProperties(variant);
	return (
		<Link className={Styles.Container} href={href} style={customProperties}>
			<Flex
				alignItems="center"
				direction="column"
				gap={designTokens["spacing/m"]}
				justifyContent="center"
			>
				{variant === "default" ? (
					<span className={Styles.Label} style={customProperties}>
						{label}
					</span>
				) : (
					<Flex alignItems="center" gap={designTokens["spacing/xxxs"]}>
						{variant === "past" ? (
							<RiErrorWarningFill className={Styles.Icon} />
						) : (
							<RiAlertFill className={Styles.Icon} />
						)}
						<span className={Styles.Label} style={customProperties}>
							{label}
						</span>
					</Flex>
				)}
				<span className={Styles.Count} style={customProperties}>
					{count}
				</span>
			</Flex>
		</Link>
	);
};
